import React from "react";
import { TestID } from "../../shared/testids/testids";
import "../../COREDesignSystem/Layout/COREPageHeader.less";
import { COREIcon } from "../../COREDesignSystem/Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { COREButton } from "../../COREDesignSystem/Action/COREButton";
import { yellow100 } from "../../COREDesignSystem/Content/COREColour";
import { useUserDashboard } from "../../shared/customHoooks/useUserDashboard";
import { useUserAccessibleSections } from "../../shared/customHoooks/useUserAccessibleSections";
import { UserAccessibleSection } from "../../openapi-typescript/common/user_accessible_section";
import { matchPath, useLocation } from "react-router-dom";
import { DEFAULT_HOMEPAGE_URL } from "../../shared/global";
import { routesWithParams } from "../../COREDesignSystem/Layout/COREPageHeader";

export const checkHomePagePermission = (
  sections: UserAccessibleSection[],
  path: string
): boolean => {
  return sections.some((section) => {
    const matchesPath =
      (path && path === section.callToActionUrl) ||
      path === "/dashboard" ||
      routesWithParams.find((route) => matchPath({ path: route }, path));

    const hasChildrenPermission =
      section.children?.length > 0
        ? checkHomePagePermission(section.children, path)
        : false;

    return matchesPath || hasChildrenPermission;
  });
};

export const useHomePage = () => {
  const location = useLocation();
  const path = decodeURIComponent(location.pathname ?? "");

  const {
    sync: syncMenu,
    loading: loadingMenu,
    error: errorMenu,
    userAccessibleSections,
  } = useUserAccessibleSections();

  const {
    sync: syncDashboard,
    loading: loadingDashboard,
    error: errorDashboard,
    configs: { homepage },
    markAsHomepage,
  } = useUserDashboard();

  const showSetupHomepage =
    path && checkHomePagePermission(userAccessibleSections, path);
  const isHomepage = homepage === path;

  const markCurrentURLAsHomepage = () => markAsHomepage(path);

  const onClickSetHomepage = () => {
    if (!isHomepage && showSetupHomepage) markCurrentURLAsHomepage();
    if (isHomepage && showSetupHomepage) markAsHomepage(DEFAULT_HOMEPAGE_URL);
  };

  const createMenuConfig = () => ({
    onClick: onClickSetHomepage,
    icon: (
      <COREIcon
        {...(isHomepage && { style: { color: yellow100 } })}
        icon={
          isHomepage
            ? icon({ name: "home", style: "solid" })
            : icon({ name: "home", style: "regular" })
        }
      />
    ),
    children: "Mark as homepage",
    closeWhenClick: true,
  });

  return {
    sync: syncMenu && syncDashboard,
    loading: loadingMenu || loadingDashboard,
    errorMenu,
    errorDashboard,
    markCurrentURLAsHomepage: () => markAsHomepage(path),
    markAsHomepage: markAsHomepage,
    isHomepage,
    showSetupHomepage,
    createMenuConfig,
  };
};

type MarkHomePageButtonProps = {
  testID: TestID;
};

export const MarkHomePageButton = ({ testID }: MarkHomePageButtonProps) => {
  const { createMenuConfig } = useHomePage();
  const config = createMenuConfig();

  return (
    <COREButton
      key="set-default-homepage-button"
      displayTooltip={true}
      tooltipWidth="auto"
      tooltipPosition="topRight"
      tooltipTitle="Mark as homepage"
      type="default"
      testID={`${testID}-default-homepage-button`}
      size="md"
      onClick={config.onClick}
      icon={config.icon}
    />
  );
};
